import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Input,
  keepOpenForTutorial,
} from '@mlm/ui';
import { useBrand } from '../../storefront/lib/hooks';
import { useTutorialStore } from '../../lib/tutorial/store';

const SAMPLE_CODE = 'SAMPLE000000';
const SAMPLE_PIN = '123456';

/**
 * The practice run's Vault row: a sample Code and PIN that walks the same screens
 * as a real redeem, carrying the same `data-tour` marks so the same tutorial
 * steps find it. It makes NO API calls, and that is the point. Every button only
 * moves this component's own state, so a practice run cannot create a slot or
 * touch a real code, whatever the member presses.
 */
export function SampleRedeem() {
  const brand = useBrand();
  const stop = useTutorialStore((s) => s.stop);
  const [stage, setStage] = useState<'row' | 'form' | 'confirm' | 'done'>('row');

  if (stage === 'form') {
    return (
      <div data-tour="redeem-form" className="space-y-3 bg-muted/30 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-medium">Redeem {SAMPLE_CODE}</div>
          <Badge variant="secondary">Sample</Badge>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-muted-foreground" htmlFor="sample-pin">
              PIN
            </label>
            <Input id="sample-pin" value={SAMPLE_PIN} readOnly className="bg-muted" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground" htmlFor="sample-sponsor">
              Affiliate Code
            </label>
            <Input id="sample-sponsor" value="SAMPLESPONSOR" readOnly className="bg-muted" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="button" className="flex-1" onClick={() => setStage('confirm')}>
            Continue
          </Button>
          <Button type="button" variant="outline" onClick={() => setStage('row')}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (stage === 'confirm') {
    return (
      <div data-tour="redeem-confirm" className="space-y-3 bg-muted/30 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-medium">Confirm activation</div>
          <Badge variant="secondary">Sample</Badge>
        </div>
        <Card>
          <CardContent className="space-y-1 p-3 text-sm">
            <div className="text-xs text-muted-foreground">You will be placed under</div>
            <div className="font-medium">Your sponsor's name</div>
            <div className="text-xs text-muted-foreground">
              Sponsor slot <span className="font-mono">SAMPLESPONSOR</span>
            </div>
          </CardContent>
        </Card>
        <div className="flex gap-2">
          <Button type="button" className="flex-1" onClick={() => setStage('done')}>
            Confirm &amp; activate
          </Button>
          <Button type="button" variant="outline" onClick={() => setStage('form')}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div data-tour="vault-code-row" className="space-y-2 p-3">
        <div className="flex items-baseline justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 font-medium">
              Sample {brand.kitLabel} <Badge variant="secondary">Sample</Badge>
            </div>
            <div className="text-xs text-muted-foreground">Paid slot · Activation</div>
          </div>
          <Button size="sm" data-tour="vault-redeem" onClick={() => setStage('form')}>
            Redeem
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Code</div>
            <div className="font-mono">{SAMPLE_CODE}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">PIN</div>
            <div className="font-mono">{SAMPLE_PIN}</div>
          </div>
        </div>
      </div>
      <Dialog open={stage === 'done'} onOpenChange={(o) => !o && stop()}>
        <DialogContent className="max-w-sm text-center" onInteractOutside={keepOpenForTutorial}>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
            <CheckCircle2 className="h-7 w-7 text-success" />
          </div>
          <DialogTitle className="mt-2 text-center text-h2">Slot activated</DialogTitle>
          <div
            data-tour="redeem-done"
            className="mt-1 space-y-1 rounded-md bg-muted/50 p-3 text-left text-sm"
          >
            <div>
              Slot code: <span className="font-mono">SAMPLE</span>
            </div>
            <div className="text-xs text-muted-foreground">
              This is a sample. No slot was created.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
